import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Plus, RefreshCw, Search } from "lucide-react";
import { authClient } from "@/auth/client";
import { DatabaseRefreshCard } from "@/components/admin/DatabaseRefreshCard";
import { UserAccountDialog } from "@/components/admin/UserAccountDialog";
import { UserAccountsTable } from "@/components/admin/UserAccountsTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminUsers } from "@/hooks/useAdminUsers";
import type { AdminUser } from "@/types/admin";

const allowCredentialManagement = process.env.NODE_ENV !== "production";

export function AdminUsersPage() {
  const { data: session } = authClient.useSession();
  const {
    users,
    total,
    search,
    setSearch,
    loading,
    saving,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
  } = useAdminUsers();
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | undefined>();

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadUsers(search.trim());
  };

  const openCreateDialog = () => {
    setDialogMode("create");
    setSelectedUser(undefined);
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setDialogMode("edit");
    setSelectedUser(user);
    setDialogOpen(true);
  };

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-6 px-8 py-8 font-brand">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Button variant="ghost" size="sm" asChild className="w-fit">
            <Link to="/app" search={{ tab: "utilization" }}>
              <ArrowLeft data-icon="inline-start" />
              Dashboard
            </Link>
          </Button>
          <div>
            <h1 className="text-[28px] font-semibold tracking-tight">User Accounts</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage Better Auth users and roles.
            </p>
          </div>
        </div>
        {allowCredentialManagement && (
          <Button type="button" onClick={openCreateDialog}>
            <Plus data-icon="inline-start" />
            Create User
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <form onSubmit={submitSearch} className="flex w-full gap-2 md:max-w-md">
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by email"
              aria-label="Search users by email"
            />
            <Button type="submit" variant="outline" disabled={loading}>
              <Search data-icon="inline-start" />
              Search
            </Button>
          </form>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {loading ? "Loading users" : `${users.length} of ${total} users`}
            </span>
            <Button type="button" variant="outline" size="icon-sm" onClick={() => void loadUsers()}>
              <RefreshCw />
              <span className="sr-only">Refresh users</span>
            </Button>
          </div>
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        {loading ? (
          <div className="rounded-lg border border-dashed bg-muted/20 px-6 py-12 text-center text-sm text-muted-foreground">
            Loading user accounts...
          </div>
        ) : (
          <UserAccountsTable
            users={users}
            currentUserId={session?.user.id}
            saving={saving}
            onEdit={openEditDialog}
            onDelete={deleteUser}
          />
        )}
      </div>

      <DatabaseRefreshCard />

      {dialogOpen && (
        <UserAccountDialog
          key={`${dialogMode}-${selectedUser?.id ?? "new"}`}
          mode={dialogMode}
          open={dialogOpen}
          user={selectedUser}
          saving={saving}
          onOpenChange={setDialogOpen}
          onCreate={createUser}
          onUpdate={updateUser}
        />
      )}
    </div>
  );
}
